import { KeyboardEvent, MouseEvent, ReactNode, useEffect } from "react"
import Button from "@rio-cloud/rio-uikit/Button"
import NoData from "@rio-cloud/rio-uikit/NoData"
import Notification from "@rio-cloud/rio-uikit/Notification"
import useClipboard from "@rio-cloud/rio-uikit/useClipboard"

import { formatDateColumn } from "@/helper/formatHelper"
import { Address, Shipment } from "@/model/transportorder/transportOrder"
import { getAdminConsigneeDetails, getAdminConsignorDetails, TransportChainPartyDetails } from "../transportChainDetails"

export const TransportOrderTableRow = (
    props: {
        transportOrder: Shipment,
        isSelected: boolean,
        onSelect: (transportOrder: Shipment) => void,
    }
): ReactNode => {
    const { transportOrder, isSelected, onSelect } = props
    const clipboard = useClipboard()

    useEffect(() => {
        clipboard.copied && Notification.default("Text copied to clipboard")
    }, [clipboard.copied])

    const handleSelect = () => {
        onSelect(transportOrder)
    }

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleSelect()
        }
    }

    const handleCopyExternalIdButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        clipboard.copy(transportOrder.external_identification || "")
    }
    const handleCopyIdButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        clipboard.copy(transportOrder.id)
    }
    const handleCopyBundleIdButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        clipboard.copy(transportOrder.transport_order_id)
    }
    const handleCopyAccountIdButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        clipboard.copy(transportOrder.freightForwarder.accountId || "")
    }
    const consignor = getAdminConsignorDetails(transportOrder)
    const consignee = getAdminConsigneeDetails(transportOrder)

    return (
        <tr
            className={isSelected ? "active" : undefined}
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleRowKeyDown}
            aria-selected={isSelected}
        >
            <td>
                { transportOrder.external_identification && (
                    <div className="display-flex align-items-center">
                        <div className="ellipsis-1">{transportOrder.external_identification}</div>
                        <Button
                            bsStyle='muted'
                            bsSize='xs'
                            className='hover-bg-none'
                            onClick={handleCopyExternalIdButtonClick}
                            iconName='icon-duplicate'
                            iconOnly
                        />
                    </div>
                )}
            </td>
            <td>
                <div className="display-flex align-items-center">
                    <div className="ellipsis-1">{transportOrder.id}</div>
                    <Button
                        bsStyle='muted'
                        bsSize='xs'
                        className='hover-bg-none'
                        onClick={handleCopyIdButtonClick}
                        iconName='icon-duplicate'
                        iconOnly
                    />
                </div>
            </td>
            <td>
                <div className="display-flex align-items-center">
                    <div className="ellipsis-1">{transportOrder.transport_order_id}</div>
                    <Button
                        bsStyle='muted'
                        bsSize='xs'
                        className='hover-bg-none'
                        onClick={handleCopyBundleIdButtonClick}
                        iconName='icon-duplicate'
                        iconOnly
                    />
                </div>
            </td>
            <td>
                { transportOrder.freightForwarder.accountId && (
                    <div className="display-flex align-items-center">
                        <div className="ellipsis-1">{transportOrder.freightForwarder.accountId}</div>
                        <Button
                            bsStyle='muted'
                            bsSize='xs'
                            className='hover-bg-none'
                            onClick={handleCopyAccountIdButtonClick}
                            iconName='icon-duplicate'
                            iconOnly
                        />
                    </div>
                )}
            </td>
            <td>
                <div className="display-flex flex-column ellipsis-1">
                    <span className="text-bold ellipsis-1">{transportOrder.freightForwarder.name}</span>
                    <PartyIdentifierComponent label={"Duns:"} identifier={transportOrder.freightForwarder.duns}/>
                    <PartyIdentifierComponent label={"Loli:"}
                        identifier={transportOrder.freightForwarder.supplierCode}/>
                    <AddressComponent address={transportOrder.freightForwarder.address}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column ellipsis-1">
                    <span className="text-bold ellipsis-1">{transportOrder.supplier.name}</span>
                    <PartyIdentifierComponent label={"Duns:"}
                        identifier={transportOrder.supplier.externalIdentifiers.duns}/>
                    <PartyIdentifierComponent label={"Plant:"}
                        identifier={transportOrder.supplier.externalIdentifiers.plantCode}/>
                    <PartyIdentifierComponent label={"Loli:"}
                        identifier={transportOrder.supplier.externalIdentifiers.supplierCode}/>
                </div>

            </td>
            <td>
                <div className="display-flex flex-column ellipsis-1">
                    <span className="text-bold ellipsis-1">{transportOrder.buyer?.name}</span>
                </div>
            </td>
            <td>
                <ShippingPartyComponent
                    party={consignor}
                />
            </td>
            <td>
                <ShippingPartyComponent
                    party={consignee}
                />
            </td>
            <td>{formatDateColumn(transportOrder.createdDate)}</td>
            <td>{formatDateColumn(transportOrder.confirmationDraft.updatedAt)}</td>
        </tr>
    )
}
const ShippingPartyComponent = (props: { party?: TransportChainPartyDetails }): React.ReactElement => {
    if (!props.party) {
        return <NoData text='No data' />
    }

    return (
        <div className="display-flex flex-column ellipsis-1">
            <span className="text-bold ellipsis-1">{props.party.name}</span>
            <PartyIdentifierComponent label={"Duns:"} identifier={props.party.duns}/>
            <AddressComponent address={props.party.address}/>
        </div>
    )
}

const PartyIdentifierComponent = (props: { label: string, identifier?: string }): React.ReactElement => {
    if (!props.identifier) {
        return <></>
    }

    return (
        <span className="text-bold text-italic ellipsis-1"> {props.label} {props.identifier}</span>
    )
}
const AddressComponent = (props: { address?: Address }): React.ReactElement => {
    if (!props.address) {
        return <></>
    }

    return (
        <>
            <span className="ellipsis-1">{props.address?.street}</span>
            <span className="ellipsis-1">{props.address?.postal_code} {props.address?.city}</span>
        </>
    )
}

